<template>
    <!-- Menu flutuante: Teleport para body garante que fique acima do mapa e de qualquer stacking context -->
    <Teleport to="body">
        <div
            class="vue-menu-float"
            v-if="viewMenu && props.isAuthenticated"
            :style="vueMenuRootStyle"
            @mouseenter="menuExpanded = true"
            @mouseleave="menuExpanded = false"
        >
            <a-button
                class="map-icon-btn vue-menu-btn"
                :class="{ 'map-icon-btn-active': menuExpanded }"
                @click="toggleMenu"
                title="Menu"
                aria-label="Menu"
            >
                <template #icon>
                    <MenuOutlined class="menu-icon" />
                </template>
            </a-button>

            <Transition name="menu-panel">
                <div v-show="menuExpanded" class="menu-panel menu-panel-horizontal">
                    <!-- Linha horizontal: ícone do menu + todos os itens principais -->
                    <div class="menu-panel-main-row">
                        <div
                            v-for="item in menuWithHomeAndUser"
                            :key="item.id"
                            class="menu-panel-cell"
                            :class="{ 'menu-panel-cell-has-children': item.childrens && item.childrens.length }"
                            @mouseenter="item.childrens?.length ? expandItem(item.id) : null"
                            @mouseleave="item.childrens?.length ? collapseItem(item.id) : null"
                        >
                            <!-- Item sem filhos: só clique para navegar -->
                            <span
                                v-if="item.id === 'home' || (item.route && (!item.childrens || !item.childrens.length))"
                                class="menu-panel-icon-wrap menu-panel-item-clickable"
                                :title="item.name"
                                @click="goTo(item.route)"
                            >
                                <component :is="getMenuIcon(item)" class="menu-panel-icon" />
                            </span>
                            <!-- Item com filhos: clique na rota (se tiver), expansão ao passar o mouse -->
                            <template v-else>
                                <span
                                    class="menu-panel-icon-wrap menu-panel-item-clickable"
                                    :title="item.id === 'user' && user ? (user.name || user.email || 'Usuário') : item.name"
                                    :aria-label="item.id === 'user' && user ? (user.name || user.email || 'Usuário') : item.name"
                                    @click="item.route ? goTo(item.route) : null"
                                >
                                    <component :is="getMenuIcon(item)" class="menu-panel-icon" />
                                </span>
                                <!-- Filhos em coluna vertical abaixo do ícone -->
                                <Transition name="menu-panel-side">
                                    <span v-show="expandedIds.has(item.id)" class="menu-panel-children-vertical">
                                        <span
                                            v-if="item.id === 'user'"
                                            v-for="child in item.childrens"
                                            :key="child.key"
                                            class="menu-panel-icon-wrap menu-panel-icon-wrap-sm menu-panel-item-clickable"
                                            :title="child.name"
                                            @click="onUserAction(child.key)"
                                        >
                                            <component :is="getUserChildIcon(child.key)" class="menu-panel-icon" />
                                        </span>
                                        <span
                                            v-else
                                            v-for="child in (item.childrens || []).filter(c => c.route)"
                                            :key="child.id"
                                            class="menu-panel-icon-wrap menu-panel-icon-wrap-sm menu-panel-item-clickable"
                                            :title="child.name"
                                            @click="goTo(child.route)"
                                        >
                                            <component :is="getSubmenuIcon(child, item)" class="menu-panel-icon" />
                                        </span>
                                    </span>
                                </Transition>
                            </template>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    </Teleport>

    <!-- Modal Tutoriais -->
    <a-modal
        v-model:open="isModalVisible"
        title=" "
        width="40%"
        :footer="null"
        :body-style="{ overflowX: 'hidden', borderRadius: '8px' }"
        @cancel="handleCancel"
    >
        <Transition name="slide-fade" mode="out-in">
            <div v-if="!showHtml">
                <a-divider orientation="left" style="border-color: grey">Tutoriais - {{ clientName }}</a-divider>
                <div
                    v-for="item in faqsSource"
                    :key="item.order"
                    class="faqItem"
                    @click="openFaq(item)"
                >
                    <a-row style="width: 100%">
                        <a-col :span="2">{{ item.id }}</a-col>
                        <a-col :span="22">{{ item.name }}</a-col>
                    </a-row>
                    <RightOutlined />
                </div>
            </div>
            <div v-else-if="showHtml">
                <a-button type="primary" @click="showHtml = false" class="faqBtn">
                    <ArrowLeftOutlined /> Voltar aos Tutoriais
                </a-button>
                <div style="max-height: 700px; overflow-y: auto; border: 1px solid #f0f0f0; border-radius: 4px">
                    <browser :id="faqHtml.id" :type="'faq'" />
                </div>
            </div>
        </Transition>
    </a-modal>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
    MenuOutlined,
    BankOutlined,
    FormOutlined,
    SettingOutlined,
    FileTextOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    SearchOutlined,
    FileOutlined,
    UserOutlined,
    TeamOutlined,
    ArrowLeftOutlined,
    RightOutlined,
    HomeOutlined,
    LockOutlined,
    BookOutlined,
    LogoutOutlined,
} from '@ant-design/icons-vue'
import { getCoords } from '../../../utils/userLocation'
import { antdIconsMap } from '../../../utils/antdIcons'

const props = defineProps(['user', 'isAuthenticated'])

const viewMenu = ref(true)
const menus = ref([])
const menuExpanded = ref(false)
const expandedIds = ref(new Set())

const minHomeIconSize = 16
const maxHomeIconSize = 48
const homeIconSize = ref(32)

const vueMenuRootStyle = computed(() => ({
    '--home-map-icon-size': `${homeIconSize.value}px`,
    '--home-map-icon-glyph-size': `${Math.round(homeIconSize.value * 0.5)}px`,
    '--home-map-icon-radius': `${Math.max(4, Math.round((homeIconSize.value / 32) * 6))}px`,
}))

async function loadHomeIconPersonalization() {
    try {
        const { data } = await window.axios.get('/api/configurations/personalizations')
        const value = Number(data?.data?.home_icon_size)
        if (Number.isFinite(value)) {
            homeIconSize.value = Math.min(maxHomeIconSize, Math.max(minHomeIconSize, value))
        }
    } catch (e) {
        // mantém padrão
    }
}

const USER_MENU_CHILDREN = [
    { key: 'change-password', name: 'Trocar Senha' },
    { key: 'tutorials', name: 'Tutoriais' },
    { key: 'logout', name: 'Sair' },
]

const menuWithHomeAndUser = computed(() => {
    const home = { id: 'home', name: 'Home', route: 'home' }
    const user = { id: 'user', name: 'Usuário', childrens: USER_MENU_CHILDREN }
    return [home, ...menus.value, user]
})

const ICON_BY_MENU_ID = {
    1: GlobalOutlined,
    5: CheckCircleOutlined,
    10: SearchOutlined,
    11: FileTextOutlined,
    14: SettingOutlined,
    29: BankOutlined,
    32: FormOutlined,
}
const DEFAULT_MENU_ICON = FileOutlined
const SUBMENU_ICON_BY_PARENT = {
    32: { 33: UserOutlined, 34: TeamOutlined },
}

function getMenuIcon(menu) {
    if (menu.id === 'home') return HomeOutlined
    if (menu.id === 'user') return UserOutlined
    if (menu.icon && antdIconsMap[menu.icon]) return antdIconsMap[menu.icon]
    return ICON_BY_MENU_ID[menu.id] || DEFAULT_MENU_ICON
}

function getSubmenuIcon(child, parent) {
    if (child.icon && antdIconsMap[child.icon]) return antdIconsMap[child.icon]
    if (parent.icon && antdIconsMap[parent.icon]) return antdIconsMap[parent.icon]
    const byParent = SUBMENU_ICON_BY_PARENT[parent.id]
    if (byParent && byParent[child.id]) return byParent[child.id]
    return DEFAULT_MENU_ICON
}

function getUserChildIcon(key) {
    const map = {
        'change-password': LockOutlined,
        'tutorials': BookOutlined,
        'logout': LogoutOutlined,
    }
    return map[key] || FileOutlined
}

function toggleMenu() {
    menuExpanded.value = !menuExpanded.value
}

function expandItem(menuId) {
    const next = new Set(expandedIds.value)
    next.add(menuId)
    expandedIds.value = next
}

function collapseItem(menuId) {
    const next = new Set(expandedIds.value)
    next.delete(menuId)
    expandedIds.value = next
}

function goTo(route, newTab = false) {
    if (!route) return
    const url = '/' + route
    window.open(url, newTab ? '_blank' : '_self', 'noopener,noreferrer')
}

async function onUserAction(key) {
    if (key === 'change-password') {
        window.location.href = '/configurations/users/change-password'
        return
    }
    if (key === 'tutorials') {
        isModalVisible.value = true
        await fetchData()
        return
    }
    if (key === 'logout') {
        const form = { coords: await getCoords() }
        await window.axios.post('/logout', form)
        window.location.href = '/'
    }
}

async function loadMenus() {
    try {
        const { data } = await window.axios.get('/api/configurations/menus?active=true')
        menus.value = (data?.data || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0))
    } catch (e) {
        console.error('Erro ao carregar menus:', e)
        menus.value = []
    }
}

const clientName = typeof window !== 'undefined' ? (window.clientName || '') : ''
const faqsSource = ref([])
const isModalVisible = ref(false)
const showHtml = ref(false)
const faqHtml = ref({ id: null, html: '' })

async function fetchData() {
    try {
        const { data } = await window.axios.get('/api/configurations/faqs/principais')
        faqsSource.value = (data?.data || []).sort((a, b) => (a.order || 0) - (b.order || 0))
    } catch (e) {
        console.error('Erro ao carregar FAQs:', e)
        faqsSource.value = []
    }
}

async function openFaq(item) {
    faqHtml.value = {
        id: item.id,
        html: (await window.axios.get(`/configurations/faqs/view/faq/${item.id}`)).data
    }
    showHtml.value = true
}

function handleCancel() {
    isModalVisible.value = false
    showHtml.value = false
}

function toggleFullscreen(event) {
    const page = event.detail?.page
    if (!document.fullscreenElement) {
        if (page === 'home') {
            document.documentElement.requestFullscreen().catch(err => {
                console.error(`Erro ao tentar entrar em fullscreen: ${err.message}`)
            })
            viewMenu.value = false
        }
    } else {
        viewMenu.value = true
        document.exitFullscreen()
    }
}

function onFullScreenChange() {
    if (!document.fullscreenElement) {
        viewMenu.value = true
    } else {
        viewMenu.value = false
    }
}

onMounted(async () => {
    await loadHomeIconPersonalization()
    await loadMenus()
    document.addEventListener('fullscreenchange', onFullScreenChange)
    window.addEventListener('map-fullscreen', toggleFullscreen)
})

onUnmounted(() => {
    document.removeEventListener('fullscreenchange', onFullScreenChange)
    window.removeEventListener('map-fullscreen', toggleFullscreen)
})
</script>

<style scoped>
/* Ícone flutuante: sem fundo, sem box */
.vue-menu-float {
    position: fixed;
    top: 8px;
    left: 8px;
    z-index: 9999;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    background: transparent;
    border: none;
    box-shadow: none;
    gap: 0;
}

.vue-menu-btn.map-icon-btn {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, 0.6);
    color: white;
    border: 2px solid white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.vue-menu-btn.map-icon-btn:hover {
    background-color: rgba(0, 60, 136, 0.7);
}

.vue-menu-btn.map-icon-btn-active {
    background-color: rgba(0, 60, 136, 0.85);
}

.menu-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.menu-panel {
    position: absolute;
    top: 0;
    left: 100%;
    margin-left: 6px;
    width: auto;
    min-width: 44px;
    max-height: min(70vh, 400px);
    overflow: visible;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0 4px;
    z-index: 1050;
}

.menu-panel-horizontal .menu-panel-main-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 4px;
    flex-wrap: nowrap;
}

.menu-panel-cell {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-height: calc(var(--home-map-icon-size, 32px) + 4px);
}

.menu-panel-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
}

.menu-panel-item-clickable {
    cursor: pointer;
}

.menu-panel-icon-wrap {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, 0.6);
    color: white;
    border: 2px solid white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.menu-panel-icon-wrap:hover {
    background-color: rgba(0, 60, 136, 0.7);
}

.menu-panel-children-side {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 4px;
}

.menu-panel-children-vertical {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 4px;
    padding: 4px 0;
}

.menu-panel-children-vertical .menu-panel-icon-wrap {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
}

.menu-panel-children-side .menu-panel-icon-wrap {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
}

.menu-panel-icon-wrap-sm {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
}

.menu-panel-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.menu-panel-enter-active,
.menu-panel-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.menu-panel-enter-from,
.menu-panel-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

.menu-panel-side-enter-active,
.menu-panel-side-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-panel-side-enter-from,
.menu-panel-side-leave-to {
    opacity: 0;
    transform: translateX(-6px);
}
</style>

<style>
.faqItem {
    width: 100%;
    background-color: #ededed;
    padding: 10px 20px;
    margin: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition-duration: 200ms;
    cursor: pointer;
}

.faqItem:nth-child(even) {
    background-color: #e5e5e5;
}

.faqItem:hover {
    background-color: #bababa;
}

.faqBtn {
    display: flex;
    align-items: center;
    gap: 3px;
    margin: 10px 0;
}

.slide-fade-enter-active {
    transition: all 0.3s linear;
}

.slide-fade-leave-active {
    transition: all 0.3s linear;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateX(-20px);
    opacity: 0;
}
</style>
